import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useSession } from '@/entities/session';
import type { TrpcError } from '@/shared/api';

import { DOWNLOAD_PARAM } from '../lib/gate-paths';
import { useRequestDownload } from './use-request-download';

export interface AutoDownloadState {
  /** null 이면 이 페이지 진입에 재개할 다운로드가 없었다 */
  resumedAssetId: string | null;
  isPending: boolean;
  isError: boolean;
  error: TrpcError | null;
  retry: () => void;
}

/**
 * 로그인 복귀 후 자동 재개 (LIB-U-03 AC-2·3).
 *
 * `?download=<assetId>` 를 소비하면 즉시 URL 에서 지운다 — 남겨 두면
 * 새로고침·뒤로가기마다 다운로드가 다시 시작된다. 실패 상태는 파라미터가
 * 아니라 로컬 상태로 남겨 재시도 버튼(LIB-U-04 AC-2)의 근거가 된다.
 */
export function useAutoDownload(): AutoDownloadState {
  const member = useSession((state) => state.member);
  const [searchParams, setSearchParams] = useSearchParams();
  const download = useRequestDownload();
  /** 화면이 "무엇을 재개했는지" 보여 주고, 재시도가 같은 자료를 다시 부르는 근거. */
  const [resumedAssetId, setResumedAssetId] = useState<string | null>(null);

  /**
   * **반드시 한 번만 보낸다.** StrictMode 는 effect 를 setup→cleanup→setup 으로 연달아
   * 두 번 돌리는데, 그 사이 `resumedAssetId` 는 아직 갱신되지 않아 같은 클로저가 두 번째
   * setup 도 그대로 통과한다 — 다운로드 기록이 두 줄 남고 저장도 두 번 시작된다(실측 2회).
   * state 는 다음 렌더에야 닫히지만 ref 는 그 자리에서 닫힌다.
   * `pages/common/verify-email` 이 같은 문제를 같은 방법으로 막았다.
   */
  const isFired = useRef(false);

  const requestedAssetId = searchParams.get(DOWNLOAD_PARAM);
  const { mutate } = download;

  useEffect(() => {
    if (requestedAssetId === null || member === null || isFired.current) {
      return;
    }
    isFired.current = true;
    setResumedAssetId(requestedAssetId);
    mutate({ assetId: requestedAssetId });
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete(DOWNLOAD_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [requestedAssetId, member, mutate, setSearchParams]);

  return {
    resumedAssetId,
    isPending: download.isPending,
    isError: download.isError,
    error: download.error,
    retry: () => {
      if (resumedAssetId !== null) {
        mutate({ assetId: resumedAssetId });
      }
    },
  };
}
